// Stop points for semi-automatic tracing.
//
// A stop point is a fixed catalogue entry (STOP_POINTS). The user can only turn
// each one on or off; thresholds and the hop limit come from the stand config
// (appConfig), not from the UI. Settings live in localStorage so they survive
// a reload.
//
// Settings shape: { enabled: { [key]: Boolean } }
//
// The engine reads a normalized trace context (see trace-context.js):
//   { owner, entity, isContract, hasSymbol, tags, types, description, clusterSize, txCount, loop }

import appConfig from '@/utils/appConfig'

export const STOP_POINTS_STORAGE_KEY = 'semi-auto-stop-points'
export const SEMI_AUTO_MODE_STORAGE_KEY = 'semi-auto-mode'

export const DEFAULT_MAX_HOPS = 10
export const MIN_MAX_HOPS = 1
export const MAX_MAX_HOPS = 50

// Structural limits: never configurable from the UI.
// A hop that fans out to more than one counterparty always stops the chain.
export const MAX_COUNTERPARTIES_PER_HOP = 1
// BTC: outputs of a single transaction above this → stop (legacy behaviour).
export const BTC_MAX_OUTPUTS_PER_TX = 5

// Two groups: what the reached address is labelled as, and what it does.
export const STOP_POINT_GROUPS = [
  { key: 'labels', label: 'Labels' },
  { key: 'activity', label: 'Activity' },
]

const isNum = v => typeof v === 'number' && !Number.isNaN(v)
const hasItems = v => Array.isArray(v) && v.length > 0
const text = v => (v == null ? '' : String(v)).trim()

export const STOP_POINTS = [
  // Labels — a resolved entity or a known owner: one stop point for both
  {
    key: 'entity',
    group: 'labels',
    label: 'Known entity',
    hint: 'Exchange, service, mixer or a known owner',
    defaultEnabled: true,
    evaluate: ctx => Boolean(text(ctx.entity) || text(ctx.owner)),
    reason: ctx => (text(ctx.entity) ? `known entity: ${text(ctx.entity)}` : `known owner: ${text(ctx.owner)}`),
  },
  {
    key: 'token',
    group: 'labels',
    label: 'Token contract',
    hint: '',
    defaultEnabled: true,
    evaluate: ctx => Boolean(ctx.hasSymbol),
    reason: () => 'token contract',
  },
  {
    key: 'type',
    group: 'labels',
    label: 'Type',
    hint: 'On the address or its cluster',
    defaultEnabled: true,
    evaluate: ctx => hasItems(ctx.types),
    reason: ctx => `type: ${(ctx.types || []).map(text).filter(Boolean).join(', ')}`,
  },
  {
    key: 'tag',
    group: 'labels',
    label: 'Tag',
    hint: 'On the address or its cluster',
    defaultEnabled: true,
    // a description counts as a tag too (legacy BTC semi-auto stopped on it)
    evaluate: ctx => hasItems(ctx.tags) || Boolean(text(ctx.description)),
    reason: ctx => {
      const tags = (ctx.tags || []).map(text).filter(Boolean)
      return tags.length ? `tag: ${tags.join(', ')}` : `description: ${text(ctx.description)}`
    },
  },
  // Activity
  {
    key: 'contract',
    group: 'activity',
    label: 'Smart contract',
    hint: '',
    defaultEnabled: true,
    evaluate: ctx => Boolean(ctx.isContract),
    reason: () => 'smart contract',
  },
  {
    key: 'clusterSize',
    group: 'activity',
    label: 'Large cluster',
    hint: '',
    threshold: 'clusterSize',
    defaultEnabled: true,
    evaluate: (ctx, thresholds) => isNum(ctx.clusterSize) && ctx.clusterSize >= thresholds.clusterSize,
    reason: (ctx, thresholds) => `cluster of ${ctx.clusterSize} addresses (≥ ${thresholds.clusterSize})`,
  },
  {
    key: 'txCount',
    group: 'activity',
    label: 'Active address',
    hint: '',
    threshold: 'txCount',
    defaultEnabled: true,
    evaluate: (ctx, thresholds) => isNum(ctx.txCount) && ctx.txCount >= thresholds.txCount,
    reason: (ctx, thresholds) => `${ctx.txCount} transactions on the address (≥ ${thresholds.txCount})`,
  },
  // the chain came back to an address it already passed (ctx.loop is set by
  // the chain itself); off, the return does not stop the chain, but it never
  // draws a transaction twice: when the only way on is already on the graph it
  // stops with "nothing new was found" (decided 2026-11-02)
  {
    key: 'loop',
    group: 'activity',
    label: 'Loop',
    hint: 'Back to an address this chain already passed',
    defaultEnabled: true,
    evaluate: ctx => Boolean(ctx.loop),
    reason: () => 'loop: back to an address already traced',
  },
]

export const STOP_POINT_KEYS = STOP_POINTS.map(point => point.key)

export const normalizeMaxHops = value => {
  const n = Math.round(Number(value))
  if (!isNum(n)) return DEFAULT_MAX_HOPS
  return Math.min(MAX_MAX_HOPS, Math.max(MIN_MAX_HOPS, n))
}

const stopPointByKey = STOP_POINTS.reduce((acc, point) => {
  acc[point.key] = point
  return acc
}, {})

export const getStopPoint = key => stopPointByKey[key] || null

// Thresholds and the hop limit come from the stand config; the UI never edits them.
export const getStopPointThresholds = (config = appConfig) => ({
  clusterSize: Number(config?.VUE_APP_SEMI_AUTO_CLUSTER_SIZE_THRESHOLD) || 500,
  txCount: Number(config?.VUE_APP_SEMI_AUTO_TX_COUNT_THRESHOLD) || 1000,
  maxHops: normalizeMaxHops(config?.VUE_APP_SEMI_AUTO_MAX_HOPS),
})

export const defaultEnabledMap = () =>
  STOP_POINTS.reduce((acc, point) => {
    acc[point.key] = point.defaultEnabled
    return acc
  }, {})

export const DEFAULT_STOP_POINTS = Object.freeze({
  enabled: Object.freeze(defaultEnabledMap()),
})

// Unknown keys are dropped, missing keys fall back to their defaults.
export const normalizeStopPointsSettings = raw => {
  const defaults = defaultEnabledMap()
  const enabled = {}
  const rawEnabled = raw && typeof raw.enabled === 'object' && raw.enabled ? raw.enabled : {}
  for (const key of STOP_POINT_KEYS) {
    enabled[key] = typeof rawEnabled[key] === 'boolean' ? rawEnabled[key] : defaults[key]
  }
  return { enabled }
}

export const countEnabledStopPoints = settings =>
  STOP_POINT_KEYS.filter(key => settings?.enabled?.[key]).length

const defaultStorage = () => (typeof localStorage === 'undefined' ? null : localStorage)

export const getStopPointsSettings = (storage = defaultStorage()) => {
  // an older record may carry maxHops: it is dropped, the limit is the stand's now
  try {
    const stored = storage?.getItem(STOP_POINTS_STORAGE_KEY)
    return normalizeStopPointsSettings(stored ? JSON.parse(stored) : null)
  } catch (error) {
    return normalizeStopPointsSettings(null)
  }
}

export const setStopPointsSettings = (settings, storage = defaultStorage()) => {
  const normalized = normalizeStopPointsSettings(settings)
  try {
    storage?.setItem(STOP_POINTS_STORAGE_KEY, JSON.stringify(normalized))
  } catch (error) {
    // Storage may be unavailable (private mode, quota); settings stay in memory.
  }
  return normalized
}

export const getSemiAutoModeFlag = (storage = defaultStorage()) => {
  try {
    return storage?.getItem(SEMI_AUTO_MODE_STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

export const setSemiAutoModeFlag = (value, storage = defaultStorage()) => {
  try {
    storage?.setItem(SEMI_AUTO_MODE_STORAGE_KEY, value ? 'true' : 'false')
  } catch (error) {
    // ignore, see setStopPointsSettings
  }
}

// Several addresses reached at once (the outputs of a BTC hop): the stop is
// the highest stop point in the catalogue that any of them matches, and among
// those matching the same one, the larger amount. `candidates` are
// { ctx, address, amount }. => { stopped: true, key, label, reason, address } | null
export const pickStopAmong = (candidates = [], settings, thresholds = getStopPointThresholds()) => {
  let best = null
  candidates.forEach(candidate => {
    const verdict = evaluateStopPoints(settings, candidate?.ctx, thresholds)
    if (!verdict.stopped) return
    const rank = STOP_POINT_KEYS.indexOf(verdict.key)
    const amount = Number(candidate.amount) || 0
    if (!best || rank < best.rank || (rank === best.rank && amount > best.amount)) {
      best = { ...verdict, address: candidate.address, rank, amount }
    }
  })
  if (!best) return null
  return { stopped: true, key: best.key, label: best.label, reason: best.reason, address: best.address }
}

// OR semantics: the first enabled stop point that matches wins.
// => { stopped: true, key, label, reason } | { stopped: false }
export const evaluateStopPoints = (settings, ctx, thresholds = getStopPointThresholds()) => {
  if (!ctx) return { stopped: false }
  const enabled = settings?.enabled || {}
  for (const point of STOP_POINTS) {
    if (!enabled[point.key]) continue
    try {
      if (point.evaluate(ctx, thresholds)) {
        return { stopped: true, key: point.key, label: point.label, reason: point.reason(ctx, thresholds) }
      }
    } catch (error) {
      // A broken context field must never break tracing.
      continue
    }
  }
  return { stopped: false }
}
